/**
 * This handler is in charge of community site plans in NewHomeSource Search Resul Page's map. It interacts with
 * Map Library facade for specific maps operations, like creating or removing the community site plan, however:
 * the logic about when to fetch the request to bring Community site plan's information from the server, store
 * and render them is responsibility of this handler itself.
 */
import { store, store$ } from '@redux/store';
import { 
    BehaviorSubject,
    combineLatest,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    merge,
    pairwise,
    switchMap,
    tap,
    withLatestFrom,
} from 'rxjs';
import {
    createSitePlanOverlay,
    getSitePlansOutOfViewport,
    hideMarkers,
    isSitePlanIntersectingMapBounds,
    removeSitePlanOverlay,
    showMarkers
} from 'libraries/mapLibrary';
import LoadingStatus from 'enums/loadingStatus';
import { selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
    mapMarkers,
    selectSitePlansProperties,
    selectSitePlans,
    selectFocusedListingAndSitePlans,
    selectMarkersAndSitePlans,
    selectZoomLevel,
} from '../redux/selectors';
import {
    drawSitePlan,
    getSitePlan,
    getCommunityInteractiveMapContainer,
    getCommunityStaticMapContainer,
    getSitePlanTooltip,
    getInteractiveOverlay,
    hideSitePlansTooltips,
    onMouseLeave$,
    onMouseMove$,
    onSingleClick$,
    removeActiveSitePlans,
    removeSitePlan,
    searchSitePlans$,
    } from '../services/sitePlan';
import { mapStateSlice } from '../redux/slices';
import { zoomLevelTriggers } from '../constants/sitePlansConstants';
import MarkerType from '../enums/markerType';

const { actions } = mapStateSlice;

/**
 * Basic configuration for a functional experience of site plans. It contains properties as the zoom triggers
 * for fetching and drawing the site plan, an array of the already fetched resources so they don't need
 * to be fetched again, and a loading status for the fetch operations.
 */
const configuration = {
    sitePlansReadyToRender: [],
    fetchMapsStatus: LoadingStatus.Idle,
    fetchStatus$: new BehaviorSubject(LoadingStatus.Idle),
    hoveredSitePlan: null,
    setFetchStatus(value) {
        if (this.fetchMapsStatus.name !== value.name) {
            this.fetchMapsStatus = value;
            this.fetchStatus$.next(value);
        }
    }
}

/**
 * findMarker retrieves a marker filtered by Id and type.
 * @param {Array} markers collection where we are going to find a given marker.
 * @param {int} markerId identifier for the marker we are querying.
 * @returns a marker retrieved by id.
 */
const findMarker = (markers, markerId) => {
    const preferredTypes = new Set([
        MarkerType.HousingDevelopment.toString(),
        MarkerType.BuildablePlan.toString()
    ]);
  
    return markers
      .filter(marker => marker.communityId === markerId)
      .reduce((bestMarker, marker) => {
        if (preferredTypes.has(marker.type)) {
          return preferredTypes.has(bestMarker?.type) ? bestMarker : marker;
        }
        return bestMarker || marker;
      }, null);
  };

/**
 * onNewMarkers$ observable that would emit every time there are new markers in the store.
 */
const onNewMarkers$ = store$.pipe(
    map(selectSitePlansProperties),
    filter(({ zoomLevel }) => zoomLevel),
    distinctUntilKeyChanged('isFetching'),
    filter(({ isFetching }) => !isFetching));

/**
 * onSitePlansChanged$ observable that will emit every time
 * there are changes in the site plans in the store.
 */
const onSitePlansChanged$ = store$.pipe(
    map(selectSitePlans),
    distinctUntilChanged());

/**
 * mapRemovedSitePlans function will map any site plan
 * not presented in the markers array.
 * @param {Array} markers is the current collection of markers in the map.
 * @param {Array} sitePlans is the current collection of site plans in the map.
 * @returns a collection of site plans no longer needed as there is no marker for them.
 */
const mapRemovedSitePlans = (markers, sitePlans) =>
    sitePlans.filter((sitePlan) =>
        !markers.some((marker) => marker.communityId === sitePlan.communityId));

/**
 * onRemoveNonNeededSitePlans$ observable that will emit every time new markers
 * are available in order to detect site plans not longer needed.
 */
const onRemoveNonNeededSitePlans$ = onNewMarkers$.pipe(
    withLatestFrom(onSitePlansChanged$),
    map(([{ markers }, sitePlans]) => mapRemovedSitePlans(markers, sitePlans)));

/**
 * onMapCoordinatesChanged$ observable that would emit every time map coordinates changes in the store.
 */
const onMapCoordinatesChanged$ = store$.pipe(
    map(selectSitePlansProperties),
    filter(({ zoomLevel }) => !!zoomLevel),
    filter(({ isMapLoaded }) => isMapLoaded),
    distinctUntilKeyChanged('coordinates'));

/**
 * createSitePlan function invokes map library facade with the site plan configuration to be draw.
 * @param {Object} sitePlan assiciated with a visible marker in viewport.
 */
const createSitePlan = (sitePlan) => {
    const newSitePlan = {
        communityId: sitePlan.communityId,
        isInteractiveSitePlan: sitePlan.isInteractiveSitePlan,
        sitePlanId: sitePlan.sitePlanId,
        sitePlanLayers: sitePlan.sitePlanLayers,
    };

    store.dispatch(actions.addSitePlan(newSitePlan));
}

/**
 * Dispatches new site plans to the Redux store based on the current map state and visible site plans.
 *
 * - Retrieves the current state from the Redux store.
 * - Filters markers to ensure only one marker per unique communityId is considered.
 * - Further filters these markers to only those whose community site plans are currently intersecting the map bounds.
 * - For each filtered marker, checks if its associated site plan is already shown; if not, creates and dispatches it.
 *
 * This ensures that only relevant and visible site plans are added to the store and rendered on the map,
 * preventing duplicates and unnecessary overlays.
 */
const dispatchSitePlans = ({markers, sitePlans}) => {
    const seenCommunityIds = new Set();
    const filteredMarkers = markers
      .filter(element => {
        if (seenCommunityIds.has(element.communityId)) {
          return false;
        }
        seenCommunityIds.add(element.communityId);
        return true;
      })
      .filter((marker) => {
        const communitySitePlans = configuration.sitePlansReadyToRender
            .filter((item) => item.communityId === marker.communityId);
           
        return communitySitePlans.some(isSitePlanIntersectingMapBounds);
      });

    filteredMarkers.forEach((marker) => {
        const mapConfigurations =
            configuration.sitePlansReadyToRender.filter((item) => item.communityId === marker.communityId);

        mapConfigurations.forEach((mapConfiguration) => {
            const isMapAlreadyShown = sitePlans.findIndex((item) => 
                item.communityId === mapConfiguration.communityId &&
                item.sitePlanId === mapConfiguration.sitePlanId) !== -1;

            if (isMapAlreadyShown) {
                return;
            }

            createSitePlan(mapConfiguration);
        });
    });
}

/**
 * Dispatches an action to update the site plan markers in the Redux store.
 *
 * - Receives an array of marker objects representing the current state of site plan markers.
 * - Calls the Redux action `updateSitePlanMarkers` with the provided markers array.
 * - This ensures the store and UI reflect the latest set of site plan markers.
 *
 * @param {Array} markers - The array of marker objects to update in the store.
 */
const dispatchSitePlanMarkersUpdate = (markers) => {
    store.dispatch(actions.updateSitePlanMarkers(markers));
}

/**
 * drawSitePlans function renders on the map a new site plan.
 * @param {Object} sitePlan with the id and url to render a new community site plan.
 */
const drawSitePlans = async ({ sitePlan }) => {
    const mapConfiguration = configuration.sitePlansReadyToRender.
        find((item) => sitePlan.communityId === item.communityId && sitePlan.sitePlanId === item.sitePlanId);
    
    const currentZoomLevel = selectZoomLevel(store.getState());

    const promises = [
        mapConfiguration.isInteractiveSitePlan
            ? getCommunityInteractiveMapContainer(mapConfiguration, currentZoomLevel)
            : getCommunityStaticMapContainer(mapConfiguration)
    ];

    if (selectLayoutBreakpoint(store.getState()) !== LayoutBreakpoint.Small) {
        promises.push(getSitePlanTooltip(mapConfiguration));
    }

    const [sitePlanContainer, tooltip] = await Promise.all(promises);

    if (!sitePlanContainer) {
        return;
    }

    mapConfiguration.container = sitePlanContainer;
    mapConfiguration.overlay = getInteractiveOverlay(mapConfiguration);
    mapConfiguration.tooltip = tooltip;
    mapConfiguration.polygonsContainer =
        sitePlanContainer.parentElement.querySelector('[data-polygons-container-community-id]');

    mapConfiguration.onRemove = removeSitePlan;
    mapConfiguration.onDraw = drawSitePlan;
    mapConfiguration.onSingleClick$ = onSingleClick$(mapConfiguration.overlay);
    mapConfiguration.onMouseMove$ = onMouseMove$(mapConfiguration.overlay);
    mapConfiguration.onMouseLeave$ = onMouseLeave$(mapConfiguration.overlay);

    createSitePlanOverlay(mapConfiguration);
};

/**
 * removeSitePlans removes all of the site plans.
 */
const removeSitePlans = () => {
    const visibleSitePlans = selectSitePlans(store.getState());
    visibleSitePlans.forEach((sitePlan) => {
        removeSitePlanOverlay(sitePlan.communityId);
        store.dispatch(actions.removeSitePlan(sitePlan));
    })
}

/**
 * removeSpecificSitePlans function will take a list of site plans
 * and remove them from the store and the layout.
 * @param {Array} sitePlans collection of community site plans to remove.
 */
const removeSpecificSitePlans = (sitePlans) => {
    sitePlans.forEach((sitePlan) => {
        removeSitePlanOverlay(sitePlan.communityId);
        store.dispatch(actions.removeSitePlan(sitePlan));
    })
}

/**
 * handleSitePlansInformation in a function that will create configuration objects for the retrieved
 * site plans from server side.
 * This function is async because we need to wait for the site plan's url request to be done before changing our
 * fetchMapsStatus property in this handler's configuration.
 * @param {Array} sitePlans metadata retrieved needed to render the site plan resources.
 */
const handleSitePlansInformation = ({ sitePlans, markers }) => {
    sitePlans.forEach(element => {
        const sitePlanConfiguration = {
            viewboxHeight: element.ViewBoxHeight,
            viewboxWidth: element.ViewBoxWidth,
            rotationDegrees: element.Rotation,
            svgMask: element.SvgMask,
            imageUrl: element.MapImageUrl,
            north: element.TopRightLatitude,
            south: element.BottomLeftLatitude,
            east: element.TopRightLongitude,
            west: element.BottomLeftLongitude,
            communityId: element.CommunityId,
            isInteractiveSitePlan: element.IsInteractiveSitePlan,
            sitePlanId: element.SitePlanId,
            sitePlanLayers: element.SvgLayers,
            topBottomLatitude: element.TopBottomLatitude,
            topBottomLongitude: element.TopBottomLongitude
        };

        const isSitePlanConfigurationAlreadyAdded = configuration.sitePlansReadyToRender.find(sitePlan =>
            sitePlan.communityId === sitePlanConfiguration.communityId &&
            sitePlan.sitePlanId === sitePlanConfiguration.sitePlanId
        );

        if (!isSitePlanConfigurationAlreadyAdded) {
            const marker = findMarker(markers, element.CommunityId);
            sitePlanConfiguration.communityId = marker?.communityId;
            configuration.sitePlansReadyToRender.push(sitePlanConfiguration);
        }
    });

    configuration.setFetchStatus(LoadingStatus.Succeeded);

};

/**
 * The `onMapReadyTofetchAfterCoordinatesChanged$` observable emits whenever the zoom level is valid 
 * for fetching site plan resources. It also checks that "Search Within Map" is disabled and ensures 
 * that the fetching of resources is not currently in progress or has not already been completed 
 * in a prior interaction.
 */
const onMapReadyTofetchAfterCoordinatesChanged$ = onMapCoordinatesChanged$.pipe(
    filter(({ isSearchWithinMap }) => !isSearchWithinMap),
    filter(({ markers }) => markers && markers.length > 0),
    filter(() => configuration.fetchMapsStatus.name !== LoadingStatus.Loading.toString() &&
                 configuration.fetchMapsStatus.name !== LoadingStatus.Succeeded.toString()));

/**
 * fetchResults$ is in charge of emiting site plan metadata retrieved after requesting it to the server.
 */
const fetchResults$ = merge(onMapReadyTofetchAfterCoordinatesChanged$, onNewMarkers$).pipe(
    map(({ markers }) => {
        const retrievedStaticMapIds = new Set(configuration.sitePlansReadyToRender.map(item => item.communityId));
        const uniqueMarkers = new Map();
      
        markers.forEach(marker => {
          if (!retrievedStaticMapIds.has(marker.communityId) && !uniqueMarkers.has(marker.communityId)) {
            uniqueMarkers.set(marker.communityId, marker);
          }
        });
      
        return Array.from(uniqueMarkers.values());
    }),
    filter(markers => markers.length > 0),
    tap(() => {
      configuration.setFetchStatus(LoadingStatus.Loading);
    }),
    switchMap((markers) => searchSitePlans$(markers))
  );

/**
 * onNewListings$ will stream a value every time there are new listings available.
 */
const onNewListings$ = store$.pipe(
    map(mapMarkers),
    distinctUntilChanged(),
);

/**
 * ondrawSitePlans$ observable would emit a value when there are site plans calculated
 * from the server and the map has a zoom level that meets the criteria for rendering those site plans.
 */
const ondrawSitePlans$ = combineLatest([
    configuration.fetchStatus$,
    onMapCoordinatesChanged$,
    onNewListings$
]).pipe(
    filter(([, { zoomLevel }]) => zoomLevel >= zoomLevelTriggers.zoomLevelDrawSitePlansTrigger),
    filter(([fetchStatus]) => fetchStatus.name === LoadingStatus.Succeeded.toString()),
    withLatestFrom(store$),
    map(([[, { markers }], state]) => ({
        markers: markers || [],
        sitePlans: selectSitePlans(state)
    }))
);

/**
 * onRemoveSitePlans$ observable would emit a value when the zoom level is in a threshold where we
 * preffer to show markers than site plan. When we get to that threshold: then we hide any
 * visible site plans and their respective tooltips.
 */
const onRemoveSitePlans$ = combineLatest([
    configuration.fetchStatus$,
    onMapCoordinatesChanged$
]).pipe(
    filter(([, { zoomLevel }]) => zoomLevel < zoomLevelTriggers.zoomLevelDrawSitePlansTrigger),
    filter(([fetchStatus]) => fetchStatus.name === LoadingStatus.Succeeded.toString()));

/**
 * hideSitePlanMarkers function will identify amd hide markers that needs to be
 * hidden as their site plan was created due to zoom level changes.
 * @param {Array} addedSitePlans is the collection of the current shown community site plans.
 * @param {Array} markers is the collection of the current shown markers.
 */
const hideSitePlanMarkers = (addedSitePlans, markers) => {
    const markersToHide = markers.filter(marker => 
        addedSitePlans.some(sitePlan => sitePlan.communityId === marker.communityId)
    );

    markersToHide.forEach(marker => hideMarkers(marker.communityId));
};

/**
 * showSitePlanMarkers function will identify amd show markers that needs to be
 * shown as their site plan was removed due to zoom level changes.
 * @param {Array} removedSitePlans is the collection of the removed community site plans.
 * @param {Array} markers is the collection of the current shown markers.
 */
const showSitePlanMarkers = (removedSitePlans, markers) => {
    const markersToShow = markers.filter(marker => 
        removedSitePlans.some(sitePlan => sitePlan.communityId === marker.communityId)
    );

    markersToShow.forEach(marker => showMarkers(marker.communityId));
};

/**
 * removeSitePlansNotInViewPort function will identify and remove from the map
 * those site plans that are no longer within the user viewport.
 */
const removeSitePlansNotInViewPort = ({sitePlans}) => {
    const mapsNotInViewPort = getSitePlansOutOfViewport();

    /**
     * there are site plans composed with separated pieces. If some of these pieces is not in the
     * viewport, it could be weird to remove part of the community representation. This is why we
     * are making sure to remove maps only if all of the pieces that represent a community
     * are not in the viewport.
     */
    const filteredSitePlans = mapsNotInViewPort.filter((sitePlan) => {
        const visibleSitePlans =
            sitePlans.filter((visibleMap) => visibleMap.communityId === sitePlan.communityId).length;
        const notVisibleSitePlans =
            mapsNotInViewPort.filter((NotVisibleMap) => NotVisibleMap.communityId === sitePlan.communityId).length;

        return visibleSitePlans === notVisibleSitePlans;
      });

    removeSpecificSitePlans(filteredSitePlans);
}

/**
 * handleFocusedListingOnSitePlan function will add the active class to the site plans
 * focused by the user. It could be in interactions like the active summary card listing in mobile, or the
 * hovered listing card in the grid view.
 * @param {int} communityId is the identifier of the site plans to apply the active class.
 * @param {HTML} currentTooltip html representing current tooltip.
 * @param {HTML} previousTooltip html representing previous tooltip.
 */
const handleFocusedListingOnSitePlan = ({ communityId, currentTooltip, previousTooltip }) => {
    const { isInInteractiveState } = getSitePlan({ communityId });

    if (isInInteractiveState) {
        return;
    }

    removeActiveSitePlans();

    const newActiveSitePlan = document.querySelector(`[data-community-map-wrapper-id="${communityId}"]`);
    newActiveSitePlan?.classList.add('site-plan_image-active');

    if (currentTooltip) {
        currentTooltip.removeAttribute('hidden');
    }

    if (previousTooltip) {
        previousTooltip.setAttribute('hidden', '');
    }
}
  
/**
 * The `onHoveredCard$` observable emits a value when a hovered card in the grid view corresponds 
 * to a community that has a representation on the map with a site plan. 
 * 
 * This observable is useful for displaying the hover state on the map when the user hovers 
 * over its associated card.
 */
const onHoveredCard$ = store$.pipe(
    map(selectFocusedListingAndSitePlans),
    distinctUntilKeyChanged('focusedListing'),
    filter(({ sitePlans }) => sitePlans?.length > 0),
    pairwise(),
    map(([previousMarkerInfo, currentMarkerInfo]) => {
        const previousCommunityId = previousMarkerInfo?.focusedListing?.communityId;
        const currentCommunityId = currentMarkerInfo?.focusedListing?.communityId;

        const tooltipInformation = {
            previousTooltip: previousCommunityId && document.querySelector(`[data-community-map-tooltip-id="${previousCommunityId}"]`),
            currentTooltip: currentCommunityId && document.querySelector(`[data-community-map-tooltip-id="${currentCommunityId}"]`),
            communityId: +currentCommunityId,
        };

        return tooltipInformation;
    }));

/**
 * onNewCommunitySitePlans$ observable emits a value indicating new site plans added in the store.
 */
const onNewCommunitySitePlans$ = store$.pipe(
    map(selectMarkersAndSitePlans),
    distinctUntilKeyChanged('sitePlans'),
    pairwise(),
    map(([previousState, currentState]) => {
        const newSitePlan = currentState.sitePlans.find(
        (sitePlan) => 
            !previousState.sitePlans.some((previousMap) =>
                previousMap.communityId === sitePlan.communityId &&
                previousMap.sitePlanId === sitePlan.sitePlanId)
        );
    
        return {
            marker: newSitePlan && findMarker(currentState.markers, newSitePlan.communityId),
            sitePlan: newSitePlan,
        };
    }),
    filter(({ sitePlan }) => !!sitePlan));

/**
 * onZoomChanged$ observable will emit a value every time the zoom changes and there are community
 * maps rendered in the Search Results Map.
 */
const onZoomChanged$ = store$.pipe(
    map(selectSitePlansProperties),
    distinctUntilKeyChanged('zoomLevel'),
    filter(({ sitePlans }) => sitePlans.length > 0),
    filter(({ zoomLevel }) => zoomLevel >= zoomLevelTriggers.zoomLevelDrawSitePlansTrigger),
    pairwise(),
    map(([previousState, currentState]) => {
        const zoomGesture = previousState.zoomLevel > currentState.zoomLevel ? "zoomOut" : "zoomIn";

        return {
            zoomGesture,
            zoomLevel: currentState.zoomLevel,
            sitePlans: Array.from(
                new Map(
                    currentState.sitePlans.map((sitePlan) => [sitePlan.communityId, sitePlan])
                ).values()
            ),
        }
    })
)

/**
 * Observable that emits when the site plans fetch operation has succeeded and filters for interactive site plans.
 *
 * - Listens to changes in the fetch status BehaviorSubject.
 * - Emits only when the status is 'Succeeded'.
 * - Maps to the array of site plans that are ready to render and are interactive.
 * - Emits only when the resulting array changes and is truthy.
 *
 */
const onSitePlansFetchSucceeded$ = configuration.fetchStatus$.pipe(
    filter(({ name }) => name === LoadingStatus.Succeeded.toString()),
    map(() => configuration.sitePlansReadyToRender.filter(sitePlan => sitePlan.isInteractiveSitePlan)),
    distinctUntilChanged(),
    filter(Boolean),
);

/**
 * subscribing to the store. By mapping markers and site plans, we are able to toggle markers and community
 * maps visibility depending on the current map zoom level.
 */
store$.pipe(
    map(selectMarkersAndSitePlans),
    distinctUntilKeyChanged('sitePlans'),
    pairwise(),
    map(([previousState, currentState]) => ({
        removedSitePlans: previousState.sitePlans.filter(previous =>
            !currentState.sitePlans.includes(previous)),
        addedSitePlans: currentState.sitePlans,
        markers: currentState.markers,
    })),
    tap(({ removedSitePlans, markers }) => {
        showSitePlanMarkers(removedSitePlans, markers);
    }),
    tap(({ addedSitePlans, markers }) => {
        hideSitePlanMarkers(addedSitePlans, markers);
    })).subscribe();

onHoveredCard$.subscribe(handleFocusedListingOnSitePlan);
ondrawSitePlans$.subscribe(dispatchSitePlans);
onRemoveSitePlans$.subscribe(removeSitePlans);
fetchResults$.subscribe(handleSitePlansInformation);
onRemoveNonNeededSitePlans$.subscribe(removeSpecificSitePlans);
onMapCoordinatesChanged$.subscribe(removeSitePlansNotInViewPort);
onNewCommunitySitePlans$.subscribe(drawSitePlans);
onZoomChanged$.subscribe(hideSitePlansTooltips);
onSitePlansFetchSucceeded$.subscribe(dispatchSitePlanMarkersUpdate);